import { Component, ChangeDetectionStrategy } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { MultiselectObjectManagerComponentBase } from '../../../core/component/multiselect-object-manager/multiselect-object-manager.component';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ExpandedListViewItemDefinition } from '../../../core/model/expanded-list-view-item';
import { FilterDefinitionItem, FilterDefinitionItemLocalDate } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import ListViewColumn from '../../../core/model/list-view-column';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { ListViewColumnFactory } from '../../../core/service/factory/list-view-column-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { rentalObjectCommand } from '../../command/rental-object.command';
import { vacantRentalObjectCommand } from '../../model/const/command';
import { LettingStatusKind, RentalObjectCategory, CustomPropertyVisibilityType } from '../../model/const/enums';
import { VacantRentalObjectDwelling } from '../../model/vacant-rental-object-dwelling';
import { CreateContractCommandHandler } from '../../service/command/create-contract-command-handler.service';
import { VacantCommandHandlerResolver } from '../../service/command/vacant-command-handler-resolver.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { VacantDwellingService } from '../../service/data/vacant-dwelling.service';
import { CompanyService } from '../../service/data/company.service';
import { PropertyService } from '../../service/data/property.service';
import { BuildingService } from '../../service/data/building.service';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { LanguageService } from '../../../core/service/language.service';
import { ActivatedRoute } from '@angular/router';
import { VacantListDrilldownState } from '../../helper/vacant-list-drilldown';

@Component({
  selector: 'app-vacant-period-dwelling-manager',
  templateUrl: 'vacant-period-dwelling-manager.component.html',
  styleUrls: ['vacant-period-dwelling-manager.component.scss', './../../../core/component/object-manager/object-manager.template.scss'],
  providers: [CreateContractCommandHandler, VacantCommandHandlerResolver, { provide: 'category', useValue: RentalObjectCategory.Dwelling }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VacantPeriodDwellingManagerComponent extends MultiselectObjectManagerComponentBase<VacantRentalObjectDwelling> {
  private readonly vacantDrilldown = new VacantListDrilldownState();

  constructor(
    columnDefinitionService: ColumnDefinitionService,
    private readonly _dataService: VacantDwellingService,
    dispatcher: CommandDispatcher,
    handlerResolver: VacantCommandHandlerResolver,
    messageService: MessageService,
    private readonly _languageService: LanguageService,
    navigationService: NavigationService,
    private readonly _cdf: ColumnDefinitionFactory,
    private readonly _listViewColumnFactory: ListViewColumnFactory,
    private readonly _accountService: UserAccountService,
    private _companyService: CompanyService,
    private _propertyService: PropertyService,
    private _buildingService: BuildingService,
    private readonly _ch: CustomPropertyHelper,
    private readonly _customPropertyService: CustomPropertyService,
    activatedRoute: ActivatedRoute
  ) {
    super(_dataService, dispatcher, handlerResolver, messageService, navigationService);
    this.AddSubscription(activatedRoute.queryParamMap.subscribe(params => this.vacantDrilldown.setQuery(params)));

    this.addContextMenuItemDefinition('VACANT_RENTAL_OBJECT_LIST.CREATE_LEASE_AGREEMENT_BTN', vacantRentalObjectCommand.createContract);

    this.columnDefinition = columnDefinitionService.create('VacantRentalObjectDwelling');
    this.legacyExcelColumnDefinitions = [
      this._cdf.create('COMMON.APARTMENTNUMBER', 'apartmentNumber', ColumnVisibility.optional),
      this._cdf.createNumber('RENTAL_OBJECT_TYPE_FORM.NUMBER_OF_ROOMS_LABEL', 'numberOfRooms', ColumnVisibility.optional),
      this._cdf.create('DWELLING_FORM.FLOOR_LABEL', 'floor', ColumnVisibility.optional),
      this._cdf.create('DWELLING_LEASE_AGREEMENT_FORM.ATTIC_STORAGE_NUMBER_LABEL', 'atticStorageNumber', ColumnVisibility.optional),
      this._cdf.create('DWELLING_LEASE_AGREEMENT_FORM.BASEMENT_STORAGE_NUMBER_LABEL', 'basementStorageNumber', ColumnVisibility.optional),
      this._cdf.create('DWELLING_LEASE_AGREEMENT_FORM.OTHER_STORAGE_NUMBER_LABEL', 'otherStorageNumber', ColumnVisibility.optional),
      this._cdf.create('COMMON.BUILDING', 'buildingName', ColumnVisibility.optional),
      this._cdf.createMoney('EXCEL_EXPORT_SETTINGS.CALENDAR_YEAR_RENT', 'calendarYearRent', ColumnVisibility.optional),
      this._cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'currentMonthRent', ColumnVisibility.optional),
      this._cdf.createMoney('EXCEL_EXPORT_SETTINGS.VACANT_MOVING_IN_RENT', 'movingInRent', ColumnVisibility.optional),
    ];
    this.excelColumnDefinition = [...this.columnDefinition, ...this.legacyExcelColumnDefinitions];
    this.selectedColumns = this._listViewColumnFactory.create(this.excelColumnDefinition).filter(column => column.selected);

    this.AddSubscription(
      this._accountService.current.subscribe({
        next: account => {
          const profileSettings = this._accountService.profileSettings;

          if (
            account.hasAccess(ModuleNames.Letting, Features.Letting_HomeQ) ||
            account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)
          ) {
            this.addContextMenuItemDefinition('RENTAL_OBJECT.COMMAND.ADVERTISE', rentalObjectCommand.advertise);
          }
          if (profileSettings.useDhMarket && !this.columnDefinition.some(column => column.binding === 'lettingStatusText')) {
            this.columnDefinition.splice(
              0,
              0,
              this._cdf.create('RENTAL_OBJECT.LETTING_STATUS_DHMARKET', 'lettingStatusText', ColumnVisibility.optional)
            );
            this.columnDefinition = [...this.columnDefinition];
            this.excelColumnDefinition = [...this.columnDefinition, ...this.legacyExcelColumnDefinitions];
          }
        },
      })
    );

    const tran = this._languageService.getCurrentTranslations();
    this.columnGroupDefinitions = columnDefinitionService.createWithTranslation('VacantRentalObjectGrouped', tran);

    this.title = 'VACANT_RENTAL_OBJECT_LIST.DWELLING_HEADER';

    this.preferencesKey = 'rent.vacant-period.dwelling.list';
    this.groupedPreferencesKey = 'rent.vacant-period.dwelling.grouped';

    this.menuDefinition = [
      new MenuItemDefinition('VACANT_RENTAL_OBJECT_LIST.CREATE_BLOCK_LEASE_AGREEMENT_BTN', vacantRentalObjectCommand.createBlockContract),
    ];
  }

  columnGroupDefinitions: ColumnDefinition[];
  groupedPreferencesKey: string;
  private readonly legacyExcelColumnDefinitions: ColumnDefinition<VacantRentalObjectDwelling>[];
  excelColumnDefinition: ColumnDefinition<VacantRentalObjectDwelling>[] = [];
  excelContext = 'VacantDwelling';
  excelFileName = 'Lediga_hyresobjekt_bostad';
  selectedColumns: ListViewColumn<VacantRentalObjectDwelling>[] = [];
  selectedDwellingIds: string[] = [];
  private pendingSelectedColumns: ListViewColumn<VacantRentalObjectDwelling>[] = [];
  private columnUpdateScheduled = false;

  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  ngOnInit() {
    this.AddSubscription(
      combineLatest([
        this._dataService.getFilterDefinition(),
        this._accountService.current,
        this._customPropertyService.getList(),
      ]).subscribe(([definition, _, customProperties]) => {
        definition = this.vacantDrilldown.applyToDefinition(definition);
        const profileSettings = this._accountService.profileSettings;

        this.defaultFilters = definition.defaultFields;

        const rentalObjectTypes = definition.rentalObjectTypes.map(x => {
          return { caption: x.name, value: x.id };
        });
        rentalObjectTypes.splice(0, 0, { caption: 'ALL_TEXT', value: '' });
        const yesNo = [
          { caption: 'YES_TEXT', value: 1 },
          { caption: 'NO_TEXT', value: 2 },
        ];
        yesNo.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: 0 });
        const displaySelection = [
          { caption: 'COMMON.LIST', value: 1 },
          { caption: 'REPORT.GROUP_LEVEL_PROPERTY', value: 2 },
        ];
        displaySelection.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: 0 });
        const vacantPeriodSelection = [
          { caption: 'VACANT_RENTAL_OBJECT_LIST.ONLY_VACANT_UNTIL_FURTHER_NOTICE', value: 0 },
          { caption: 'VACANT_RENTAL_OBJECT_LIST.LIMITED_IN_TIME', value: 1 },
          { caption: 'ALL_TEXT', value: 2 },
        ];
        const lettingStatusSelection = [
          { caption: 'ALL_TEXT', value: null },
          { caption: 'VACANT_RENTAL_OBJECT_LIST.LETTING_STATUS.NONE', value: LettingStatusKind.NotSpecified },
          { caption: 'VACANT_RENTAL_OBJECT_LIST.LETTING_STATUS.COMING', value: LettingStatusKind.Coming },
          { caption: 'VACANT_RENTAL_OBJECT_LIST.LETTING_STATUS.CURRENT_PUBLICATION', value: LettingStatusKind.CurrentPublication },
          { caption: 'VACANT_RENTAL_OBJECT_LIST.LETTING_STATUS.PUBLICATION_EXPIRED', value: LettingStatusKind.PublicationExpired },
          { caption: 'VACANT_RENTAL_OBJECT_LIST.LETTING_STATUS.ACTIVE_OFFERS', value: LettingStatusKind.ActiveOffers },
        ];

        this.filterDefinition = [
          new FilterDefinitionItem(
            'COMPANY.LIST_HEADER',
            'company',
            definition.company,
            this._companyService,
            definition.selectedFields.includes('company'),
            true
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
            'property',
            definition.property,
            this._propertyService,
            definition.selectedFields.includes('property'),
            true
          ),
          new FilterDefinitionItem(
            'COMMON.BUILDING',
            'building',
            definition.building,
            this._buildingService,
            definition.selectedFields.includes('building'),
            true
          ),
          new FilterDefinitionItem(
            'COMMON.RENTAL_OBJECT_TYPE',
            'rentalObjectType',
            definition.rentalObjectType,
            rentalObjectTypes,
            definition.selectedFields.includes('rentalObjectType')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_LIST.STREET_COLUMN',
            'street',
            definition.street,
            null,
            definition.selectedFields.includes('street')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_LIST.CITY_COLUMN',
            'city',
            definition.city,
            null,
            definition.selectedFields.includes('city')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN',
            'rentalId',
            definition.rentalId,
            null,
            definition.selectedFields.includes('rentalId')
          ),
          new FilterDefinitionItem(
            'VACANT_RENTAL_OBJECT_LIST.DISPLAY_VACANT_PERIODS',
            'vacantPeriod',
            definition.vacantPeriod,
            vacantPeriodSelection,
            definition.selectedFields.includes('vacantPeriod')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT.ACTIVE_BLOCKING',
            'blockedRentalObjects',
            definition.blockedRentalObjects,
            yesNo,
            definition.selectedFields.includes('blockedRentalObjects')
          ),
          new FilterDefinitionItem(
            'COMMON.DISPLAY',
            'displayType',
            definition.displayType,
            displaySelection,
            definition.selectedFields.includes('displayType')
          ),
          new FilterDefinitionItemLocalDate(
            'VACANT_RENTAL_OBJECT_LIST.VACANT_ON_DATE',
            'vacantOnDate',
            definition.vacantOnDate,
            definition.selectedFields.includes('vacantOnDate')
          ),
        ];
        if (profileSettings.useDhMarket) {
          this.filterDefinition.push(
            new FilterDefinitionItem(
              'RENTAL_OBJECT.LETTING_STATUS_DHMARKET',
              'lettingStatus',
              definition.lettingStatus,
              lettingStatusSelection,
              definition.selectedFields.includes('lettingStatus')
            )
          );
        }
        const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
          customProperties,
          definition,
          CustomPropertyVisibilityType.DwellingAndCompany
        );

        this.columnDefinition = this.columnDefinition.filter(column => !column.binding?.startsWith('rentalObjectCustomProperties'));
        this._ch.createCustomPropertyColumnDefinition(customProperties, this.columnDefinition, CustomPropertyVisibilityType.Dwelling);
        this.columnDefinition = [...this.columnDefinition];
        this.excelColumnDefinition = [...this.columnDefinition, ...this.legacyExcelColumnDefinitions];

        this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters];
      })
    );
  }

  dataExpandedList: ExpandedListViewItemDefinition[];

  private overviewSubscription: Subscription;

  currentDisplayType: number;

  get displayGroupedByProperty(): boolean {
    return this.currentDisplayType && this.currentDisplayType === 2;
  }

  get displayList(): boolean {
    return this.itemsSource && (this.currentDisplayType === undefined || this.currentDisplayType === 0 || this.currentDisplayType === 1);
  }

  onFilterChange(filterState: any) {
    if (!filterState.selectedFields || this.filterDefinition === undefined) return;
    filterState = this.vacantDrilldown.prepareFilter(filterState);

    this.filterState = filterState;
    this.selectedDwellingIds = [];
    this.currentDisplayType = filterState.displayType;

    if (filterState.displayType === undefined || filterState.displayType === 0 || filterState.displayType === 1) {
      this.dataExpandedList = undefined;
      return super.onFilterChange(filterState);
    }

    this.filterInternal(filterState);
    this.itemsSource = undefined;

    if (this.overviewSubscription) {
      this.overviewSubscription.unsubscribe();
    }

    if (filterState.displayType === 2) {
      this.overviewSubscription = this._dataService.getVacancyOverview(filterState).subscribe(data => {
        this.dataExpandedList = data.map(overview => {
          const model = new ExpandedListViewItemDefinition();
          model.caption = overview.propertyNumber + ' ' + overview.propertyName + ' ' + overview.propertyMunicipality;
          model.columnDefinitions = [
            this._cdf.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
            this._cdf.create('VACANT_RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
            this._cdf.create('VACANT_RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
            this._cdf.createNumber('VACANT_RENTAL_OBJECT_LIST.SURFACE_COLUMN', 'surface', {
              visibility: ColumnVisibility.optional,
              totalSum: true,
            }),
            this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_FROM_COLUMN', 'vacantFrom'),
            this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_TO_COLUMN', 'vacantTo'),
            this._cdf.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
            this._cdf.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
            this._cdf.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
            this._cdf.createMoney('VACANT_RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent'),
            this._cdf.createMoney('COMMON.YEAR_RENT', 'yearRent'),
          ];
          model.dataContext = overview;
          model.icon = 'account-statement';
          model.itemsSource = overview.vacantRentalObjects.map(x => {
            return {
              rentalObjectId: x.rentalObjectId,
              rentalId: x.rentalId,
              address: x.address,
              rentalObjectType: x.rentalObjectType,
              surface: x.surface,
              vacantFrom: x.vacantFrom,
              vacantTo: x.vacantTo,
              blockedFrom: x.blockedFrom,
              blockedTo: x.blockedTo,
              blockingReason: x.blockingReason,
              monthlyRent: x.monthlyRent,
              yearRent: x.yearRent,
            };
          });
          return model;
        });
      });
    } else {
      this.dataExpandedList = undefined;
    }
  }

  get excelReady(): boolean {
    return !!this.filterState && this.selectedColumns.length > 0;
  }

  get selectedDwellingCount(): number {
    return this.selectedDwellingIds.length;
  }

  get allDwellingsSelected(): boolean {
    return this.selectedDwellingCount > 0 && this.selectedDwellingCount === this.itemsSource?.length;
  }

  get totalDwellings(): number {
    return this.itemsSource?.length ?? 0;
  }

  onColumnChange(columns: ListViewColumn<VacantRentalObjectDwelling>[]): void {
    this.pendingSelectedColumns = columns.filter(column => column.selected);
    if (this.columnUpdateScheduled) return;

    this.columnUpdateScheduled = true;
    queueMicrotask(() => {
      this.columnUpdateScheduled = false;
      this.selectedColumns = this.pendingSelectedColumns;
    });
  }

  onSelectedDwellingsChange(dwellings: VacantRentalObjectDwelling[]): void {
    this.selectedDwellingIds = this.displayGroupedByProperty ? [] : dwellings.map(dwelling => dwelling.id);
  }

  protected clearAdditionalSubscriptions() {
    super.clearAdditionalSubscriptions();
    if (this.overviewSubscription) this.overviewSubscription.unsubscribe();
  }

  onRowClicked(e: { context: any; event: any }): void {
    const page = this.navigation.getParameterValue('page');
    this.navigation.navigate(['vacantdwelling', 'edit', e.context.rentalObjectId],
      page && /^\d+$/.test(page) ? { queryParams: { listPage: page } } : undefined, e.event.ctrlKey);
  }
}
